import React from 'react';

import { FolderIcon } from '@vlabs/icons';
import { FiltersHeader } from '@vlabs/shared/components/filters-panel/FiltersHeader';
import { SideMenu, Group } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import { AppliedFiltersBadge, EventTypeVisibilityButton } from './components';
import {
  AgeFilterWithRightPopover,
  ApparentAgeFilterWithRightPopover,
  ApparentGenderFilterWithRightPopover,
  BackpackFilterWithRightPopover,
  GenderFilterWithRightPopover,
  GeolocationFilterWithRightPopover,
  HeadwearFilterWithRightPopover,
  HandlersFilterWithRightPopover,
  ListsFilterWithRightPopover,
  LowerFilterWithRightPopover,
  SimilarityFilterWithRightPopover,
  ShoesFilterWithRightPopover,
  SourcesFilterWithRightPopover,
  UpperFilterWithRightPopover,
} from './side-menu';
import { useWorkspaceFilters } from './useWorkspaceFilters';
import { getDetectionAndMatchFilterActivity } from './workspaceFilterActivity';
import { useWorkspaceTranslation } from '../i18n';
import st from './WorkspaceFiltersPanel.module.sass';

interface DetectionAndMatchFiltersPanelProps {
  onBackClick: VoidFunction;
}

function DetectionAndMatchFiltersPanel({
  onBackClick,
}: DetectionAndMatchFiltersPanelProps): React.JSX.Element {
  const { t } = useTranslation();
  const { t: tWorkspace } = useWorkspaceTranslation();
  const { filters, updateFilters } = useWorkspaceFilters();
  const activity = getDetectionAndMatchFilterActivity(filters);
  const isEnabled = filters.isEnabledDetectionAndMatchEvents;

  const getBadge = (isApplied: boolean) => (
    <AppliedFiltersBadge
      isApplied={isApplied}
      isEnabled={isEnabled}
    />
  );

  return (
    <Group
      gapSize="0"
      vertical
    >
      <FiltersHeader
        customTitle={t('lunapoint:фильтры.обнаружение и совпадение')}
        mode="goBack"
        onClick={onBackClick}
        rightIcon={(
          <EventTypeVisibilityButton
            isEnabled={isEnabled}
            onChange={(nextIsEnabled) => updateFilters({
              isEnabledDetectionAndMatchEvents: nextIsEnabled,
            })}
            testId="workspace.detectionAndMatchVisibility"
          />
        )}
      />

      <SideMenu.MenuItem
        data-testid="workspace.commonFilters"
        isOpened={false}
        leftIcon={<FolderIcon />}
        rightIcon={(
          <AppliedFiltersBadge
            count={activity.commonCount}
            isEnabled={isEnabled}
          />
        )}
        value={t('general')}
      >
        <SourcesFilterWithRightPopover
          badge={getBadge(activity.sources)}
          testId="sourcesFilter"
          value={t('source')}
        />
        <HandlersFilterWithRightPopover
          badge={getBadge(activity.handlers)}
          testId="handlersFilter"
          value={t('сценарии')}
        />
        <SimilarityFilterWithRightPopover
          badge={getBadge(activity.similarity)}
          testId="similarityFilter"
          value={t('similarity')}
        />
        <GeolocationFilterWithRightPopover
          badge={getBadge(activity.location)}
          popoverProps={{
            idPrefix: 'workspace.detection',
          }}
          testId="geolocationFilter"
          value={tWorkspace('geolocation_filter.title')}
        />
        <ListsFilterWithRightPopover
          badge={getBadge(activity.lists)}
          testId="listsFilter"
          value={t('list')}
        />
      </SideMenu.MenuItem>

      <div className={st.Divider} />

      <SideMenu.MenuItem
        data-testid="workspace.faceAttributesFilters"
        leftIcon={<FolderIcon />}
        rightIcon={(
          <AppliedFiltersBadge
            count={activity.faceCount}
            isEnabled={isEnabled}
          />
        )}
        value={t('events:filter form.face attributes and properties')}
      >
        <GenderFilterWithRightPopover
          badge={getBadge(activity.gender)}
          testId="genderFilter"
          value={t('gender')}
        />
        <AgeFilterWithRightPopover
          badge={getBadge(activity.age)}
          testId="ageFilter"
          value={t('age')}
        />
      </SideMenu.MenuItem>

      <div className={st.Divider} />

      <SideMenu.MenuItem
        data-testid="workspace.bodyAttributesFilters"
        leftIcon={<FolderIcon />}
        rightIcon={(
          <AppliedFiltersBadge
            count={activity.bodyCount}
            isEnabled={isEnabled}
          />
        )}
        value={t('events:filter form.body attributes and properties')}
      >
        <ApparentAgeFilterWithRightPopover
          badge={getBadge(activity.apparentAge)}
          testId="apparentAgeFilter"
          value={t('events:body basic attributes.apparent age')}
        />
        <ApparentGenderFilterWithRightPopover
          badge={getBadge(activity.apparentGender)}
          testId="apparentGenderFilter"
          value={t('events:body basic attributes.apparent gender')}
        />
        <LowerFilterWithRightPopover
          badge={getBadge(activity.lower)}
          testId="lowerFilter"
          value={t('events:карточка.низ')}
        />
        <UpperFilterWithRightPopover
          badge={getBadge(activity.upper)}
          testId="upperFilter"
          value={t('events:карточка.верх')}
        />
        <HeadwearFilterWithRightPopover
          badge={getBadge(activity.headwear)}
          testId="headwearFilter"
          value={t('events:headwear')}
        />
        <ShoesFilterWithRightPopover
          badge={getBadge(activity.shoes)}
          testId="shoesFilter"
          value={t('events:shoes_colors')}
        />
        <BackpackFilterWithRightPopover
          badge={getBadge(activity.backpack)}
          testId="backpackFilter"
          value={t('events:backpack')}
        />
      </SideMenu.MenuItem>

      <div className={st.Divider} />
    </Group>
  );
}

export default DetectionAndMatchFiltersPanel;
